import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { ServiceAttributeDefinition, ServiceCategory } from '../types/Service';
import { createService, getServiceCategories, serviceCatalogService } from '../services/serviceCatalogService';
import { CategorySelect } from '../../catalog/CategorySelect';
import { CatalogAttributeFields } from '../../catalog/CatalogAttributeFields';
import { useTenantCurrency } from '../../invoices/tenantCurrency';
import styles from '../../catalog/Catalog.module.css';

const parseNumber = (value: string): number | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const AddService: React.FC = () => {
  const { t } = useTranslation(['services', 'common']);
  const navigate = useNavigate();
  const currency = useTenantCurrency();
  const [categories, setCategories] = React.useState<ServiceCategory[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [generalError, setGeneralError] = React.useState<string | null>(null);
  const [form, setForm] = React.useState({
    name: '',
    description: '',
    categoryId: '',
    code: '',
    costPrice: '',
    markupPercent: '',
    salePrice: '',
    taxPercent: '',
    durationMinutes: '',
    accountingCode: '',
  });
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [definitions, setDefinitions] = React.useState<ServiceAttributeDefinition[]>([]);
  const [attributes, setAttributes] = React.useState<Record<string, string>>({});

  React.useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await getServiceCategories();
        setCategories(data);
        if (data.length === 1) {
          setForm(prev => ({ ...prev, categoryId: data[0].id }));
        }
      } catch {
        setGeneralError(t('failedToLoadCategories', { ns: 'services' }));
      } finally {
        setLoading(false);
      }
    };

    void load();
  }, [t]);

  React.useEffect(() => {
    if (!form.categoryId) {
      setDefinitions([]);
      setAttributes({});
      return;
    }

    let cancelled = false;
    void serviceCatalogService.getAttributes(form.categoryId, true)
      .then(items => {
        if (cancelled) return;
        setDefinitions(items);
        setAttributes(prev => {
          const next: Record<string, string> = {};
          items.forEach(item => {
            if (prev[item.id]) next[item.id] = prev[item.id];
          });
          return next;
        });
      })
      .catch(() => {
        if (!cancelled) {
          setDefinitions([]);
          setGeneralError(t('failedToLoadAttributes', { ns: 'services' }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [form.categoryId, t]);

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!form.name.trim()) nextErrors.name = t('nameRequired', { ns: 'services' });
    if (!form.categoryId) nextErrors.categoryId = t('categoryRequired', { ns: 'services' });
    definitions.forEach(definition => {
      if (definition.isRequired && !(attributes[definition.id] || '').trim()) {
        nextErrors[definition.id] = t('attributeRequired', { ns: 'services', name: definition.name });
      }
    });
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!validate()) return;
    setSaving(true);
    setGeneralError(null);
    try {
      const created = await createService({
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        categoryId: form.categoryId,
        code: form.code.trim() || undefined,
        costPrice: parseNumber(form.costPrice),
        markupPercent: parseNumber(form.markupPercent),
        salePrice: parseNumber(form.salePrice),
        taxPercent: parseNumber(form.taxPercent),
        durationMinutes: parseNumber(form.durationMinutes),
        accountingCode: form.accountingCode.trim() || undefined,
        attributes: Object.fromEntries(definitions.map(item => [item.id, attributes[item.id]?.trim() || null])),
      });
      if (!created) {
        setGeneralError(t('failedToSave', { ns: 'services' }));
        return;
      }
      navigate(`/services/${created.id}`);
    } catch (error) {
      setGeneralError(error instanceof Error ? error.message : t('failedToSave', { ns: 'services' }));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ display: 'flex', justifyContent: 'center', marginTop: 40 }}><Spinner text={t('loading', { ns: 'services' })} /></div>;
  }

  return (
    <div className={styles.page}>
      <Breadcrumb items={[
        { label: t('title', { ns: 'services' }), path: '/services' },
        { label: t('addTitle', { ns: 'services' }), path: '/services/add' },
      ]} />
      <h2 className={styles.title}>{t('addTitle', { ns: 'services' })}</h2>
      {categories.length === 0 ? <div className="error">{t('createCategoryFirst', { ns: 'services' })}</div> : null}
      {generalError ? <div className="error">{generalError}</div> : null}

      <form className={styles.form} onSubmit={onSubmit}>
        <div className={styles.field}>
          <label className={styles.label}>{t('name', { ns: 'services' })}</label>
          <input className={styles.input} value={form.name} placeholder={t('namePlaceholder', { ns: 'services' })} onChange={event => setForm(prev => ({ ...prev, name: event.target.value }))} />
          {errors.name ? <div className={styles.fieldError}>{errors.name}</div> : null}
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('category', { ns: 'services' })}</label>
          <CategorySelect
            className={styles.input}
            categories={categories}
            value={form.categoryId}
            onChange={categoryId => setForm(prev => ({ ...prev, categoryId }))}
            emptyLabel={t('selectCategory', { ns: 'services' })}
          />
          <div className={styles.fieldHint}>{t('categoryHint', { ns: 'services' })}</div>
          {errors.categoryId ? <div className={styles.fieldError}>{errors.categoryId}</div> : null}
        </div>
        <CatalogAttributeFields
          ns="services"
          definitions={definitions}
          values={attributes}
          errors={errors}
          onChange={(attributeId, value) => setAttributes(prev => ({ ...prev, [attributeId]: value }))}
        />
        <div className={`${styles.field} ${styles.fieldFull}`}>
          <label className={styles.label}>{t('description', { ns: 'services' })}</label>
          <textarea className={styles.textarea} value={form.description} onChange={event => setForm(prev => ({ ...prev, description: event.target.value }))} />
          <div className={styles.fieldHint}>{t('descriptionHint', { ns: 'services' })}</div>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('code', { ns: 'services' })}</label>
          <input className={styles.input} value={form.code} onChange={event => setForm(prev => ({ ...prev, code: event.target.value }))} />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('accountingCode', { ns: 'services' })}</label>
          <input className={styles.input} value={form.accountingCode} onChange={event => setForm(prev => ({ ...prev, accountingCode: event.target.value }))} />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('durationMinutes', { ns: 'services' })}</label>
          <input className={styles.input} type="number" min="0" step="1" value={form.durationMinutes} onChange={event => setForm(prev => ({ ...prev, durationMinutes: event.target.value }))} />
          <div className={styles.fieldHint}>{t('durationHint', { ns: 'services' })}</div>
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('costPrice', { ns: 'services' })} ({currency})</label>
          <input className={styles.input} type="number" min="0" step="0.01" value={form.costPrice} onChange={event => setForm(prev => ({ ...prev, costPrice: event.target.value }))} />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('markupPercent', { ns: 'services' })}</label>
          <input className={styles.input} type="number" min="0" step="0.01" value={form.markupPercent} onChange={event => setForm(prev => ({ ...prev, markupPercent: event.target.value }))} />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('salePrice', { ns: 'services' })} ({currency})</label>
          <input className={styles.input} type="number" min="0" step="0.01" value={form.salePrice} onChange={event => setForm(prev => ({ ...prev, salePrice: event.target.value }))} />
        </div>
        <div className={styles.field}>
          <label className={styles.label}>{t('taxPercent', { ns: 'services' })}</label>
          <input className={styles.input} type="number" min="0" max="100" step="0.01" value={form.taxPercent} onChange={event => setForm(prev => ({ ...prev, taxPercent: event.target.value }))} />
        </div>
        <div className={styles.actions}>
          <button type="button" className="btn btn-secondary" onClick={() => navigate('/services')} disabled={saving}>{t('back', { ns: 'services' })}</button>
          <button type="submit" className="btn btn-primary" disabled={saving || categories.length === 0}>{t('save', { ns: 'services' })}</button>
        </div>
      </form>
      <p className={styles.fieldHint}>{t('photoAfterSave', { ns: 'services' })}</p>
    </div>
  );
};

export default AddService;
